import { z } from "zod";

import { OpaqueIdSchema } from "./identity-schemas";

/**
 * The sign-in methods a workspace can offer (ADR-0061).
 *
 * `email_password` is the platform's own credential. The other two are directories, and each one
 * already had a gate before this policy existed — deployment credentials for Google, an enabled
 * connection for SAML. The policy sits above those gates and can only subtract from them.
 */
export const SignInMethodSchema = z.enum(["email_password", "google_sso", "saml_sso"]);

/**
 * What an administrator chose. These are intentions, not capabilities: `samlSso` may be true in a
 * workspace that has no enabled connection, in which case the method is switched on and still does
 * not work. `WorkspaceSignInAvailabilitySchema` is what says whether it works.
 */
export const WorkspaceSignInPolicySchema = z.strictObject({
  emailPassword: z.boolean(),
  googleSso: z.boolean(),
  samlSso: z.boolean(),
  /**
   * Whether a sign-in must begin at this workspace's own address (ADR-0063).
   *
   * The one field here that is about a place rather than a method: it constrains where any of the
   * three above may be used, and applies to all of them equally. A workspace holding
   * `acme.das.dev` can say the neutral shell is not a door into it.
   */
  requireWorkspaceHost: z.boolean(),
});

/**
 * Whether each directory method *could* work in this workspace right now, independent of what the
 * administrator chose.
 *
 * The panel shows this beside the toggles so a workspace cannot switch on a method that has no
 * chance of authenticating anyone and believe it has a way in. It is also what the save rule is
 * evaluated against, so the two can never disagree.
 */
export const WorkspaceSignInAvailabilitySchema = z.strictObject({
  /** Whether this deployment has Google client credentials configured at all. */
  googleConfigured: z.boolean(),
  /** Whether this workspace has a SAML connection at `status = 'enabled'`. */
  samlConnectionEnabled: z.boolean(),
  /**
   * Whether this workspace has an address of its own — a primary `workspace_hosts` row.
   *
   * Requiring a workspace address without holding one would name a door that does not exist, so
   * this is to `requireWorkspaceHost` what the two flags above are to the directory methods.
   */
  workspaceHostConfigured: z.boolean(),
});

export const WorkspaceSignInPolicyViewSchema = z.strictObject({
  organizationId: OpaqueIdSchema,
  policy: WorkspaceSignInPolicySchema,
  availability: WorkspaceSignInAvailabilitySchema,
  /**
   * This workspace's own address, when it has one, so the panel can name what it is about to
   * require. Derived from platform state on the server rather than assembled in the browser, which
   * is what keeps the address an administrator reads and the one a refusal sends people to the
   * same string.
   */
  workspaceOrigin: z.url().nullable(),
});

export const WorkspaceSignInPolicyUpdateSchema = WorkspaceSignInPolicySchema;

/**
 * The methods a login page should actually offer, already reduced by both the policy and the
 * availability behind it.
 *
 * The server sends the conclusion rather than the inputs so the login page cannot reach a different
 * answer than the authentication path does. Every one of these is re-checked server-side when
 * credentials or an assertion arrive; this only decides what is drawn.
 */
export const WorkspaceSignInOptionsSchema = z.strictObject({
  emailPassword: z.boolean(),
  googleSso: z.boolean(),
  samlSso: z.boolean(),
});

/**
 * Whether at least one method that can actually authenticate somebody survives a policy.
 *
 * Shared by the panel and by the repository that persists the change, so the button a user sees
 * disabled and the write the server refuses are the same rule rather than two copies of it.
 */
export function hasWorkingSignInMethod(
  policy: z.infer<typeof WorkspaceSignInPolicySchema>,
  availability: z.infer<typeof WorkspaceSignInAvailabilitySchema>,
): boolean {
  if (policy.emailPassword) return true;
  if (policy.googleSso && availability.googleConfigured) return true;
  return policy.samlSso && availability.samlConnectionEnabled;
}

/**
 * Whether a policy can be saved at all: a method that works, reachable from somewhere.
 *
 * Deliberately a second predicate rather than a fourth clause inside `hasWorkingSignInMethod`.
 * That function answers "can anything here authenticate somebody", which is what both the SSO copy
 * and the `sign_in_no_working_method` refusal say; requiring a workspace address is a separate way
 * for a save to be impossible, with its own refusal and its own sentence to the administrator.
 * Folding them together would make one message wrong for half the cases it covered.
 */
export function signInPolicyIsSatisfiable(
  policy: z.infer<typeof WorkspaceSignInPolicySchema>,
  availability: z.infer<typeof WorkspaceSignInAvailabilitySchema>,
): boolean {
  if (!hasWorkingSignInMethod(policy, availability)) return false;
  return !policy.requireWorkspaceHost || availability.workspaceHostConfigured;
}

export type SignInMethod = z.infer<typeof SignInMethodSchema>;
export type WorkspaceSignInPolicy = z.infer<typeof WorkspaceSignInPolicySchema>;
export type WorkspaceSignInAvailability = z.infer<typeof WorkspaceSignInAvailabilitySchema>;
export type WorkspaceSignInPolicyView = z.infer<typeof WorkspaceSignInPolicyViewSchema>;
export type WorkspaceSignInPolicyUpdate = z.infer<typeof WorkspaceSignInPolicyUpdateSchema>;
export type WorkspaceSignInOptions = z.infer<typeof WorkspaceSignInOptionsSchema>;
